
import { useMemo } from "react";

interface ResultDisplayProps {
  numbers: number[];
  isComplete: boolean;
}

const BALL_COLORS = [
  { bg: "from-amber-400 to-amber-600", shadow: "shadow-amber-400/50", label: "1st" },
  { bg: "from-rose-400 to-rose-600", shadow: "shadow-rose-400/50", label: "2nd" },
  { bg: "from-emerald-400 to-emerald-600", shadow: "shadow-emerald-400/50", label: "3rd" },
  { bg: "from-sky-400 to-sky-600", shadow: "shadow-sky-400/50", label: "4th" },
  { bg: "from-violet-400 to-violet-600", shadow: "shadow-violet-400/50", label: "5th" },
  { bg: "from-orange-400 to-orange-600", shadow: "shadow-orange-400/50", label: "6th" },
];

export default function ResultDisplay({ numbers, isComplete }: ResultDisplayProps) {
  const sorted = useMemo(() => [...numbers].sort((a, b) => a - b), [numbers]);

  const displayedNumbers = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => numbers[i] ?? null);
  }, [numbers]);

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border-2 border-amber-600/40 shadow-lg shadow-amber-900/20">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center text-amber-400">
          <i className="ri-trophy-fill text-lg" />
        </div>
        <h3 className="font-bold text-amber-400 text-sm tracking-wider uppercase" style={{ fontFamily: "Orbitron, sans-serif" }}>
          Selected Numbers
        </h3>
      </div>

      <div className="flex flex-wrap gap-3 justify-center mb-4">
        {displayedNumbers.map((number, i) => {
          const hasNumber = typeof number === "number" && !Number.isNaN(number);
          const colorSet = BALL_COLORS[i];
          const ballClasses = hasNumber
            ? `bg-gradient-to-br ${colorSet.bg} text-white shadow-lg ${colorSet.shadow}`
            : "bg-gray-700/50 text-gray-600 border-2 border-dashed border-gray-600";

          return (
            <div key={i} className="flex flex-col items-center gap-1">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-500 ${ballClasses} ${hasNumber ? "scale-100" : "scale-90"}`}
                style={{ fontFamily: "Orbitron, sans-serif", animationDelay: `${i * 100}ms` }}
              >
                {hasNumber ? number : "?"}
              </div>
              <span className="text-[10px] text-gray-500" style={{ fontFamily: "Orbitron, sans-serif" }}>
                {colorSet.label}
              </span>
            </div>
          );
        })}
      </div>

      {isComplete && (
        <div className="mt-3 p-3 bg-gradient-to-r from-amber-900/40 to-orange-900/40 rounded-xl border border-amber-500/30">
          <div className="flex items-center gap-2 text-amber-400 mb-1">
            <i className="ri-sparkling-2-fill text-base" />
            <span className="font-bold text-sm" style={{ fontFamily: "Orbitron, sans-serif" }}>COMPLETE!</span>
          </div>
          <p className="text-xs text-amber-200/80">
            정렬:{" "}
            <span className="font-bold text-amber-300 tracking-wider" style={{ fontFamily: "Orbitron, sans-serif" }}>
              {sorted.join(" - ")}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
